import apiClient from './client'
import {
  Driver,
  DriverStatus,
  DriverApprovalStatus,
  PaginatedResponse,
} from '../types'

const ENDPOINT = '/drivers'

export interface DriverCreateRequest {
  name: string
  phone: string
  vehicle_type: string
  vehicle_plate: string
  vehicle_color?: string
  vehicle_brand_model?: string
  engine_number?: string
  license_number: string
  license_expiry: string
  address?: string
  vehicle_inspection_date?: string
  tenant_id?: string
}

export interface VehicleItem {
  plate: string
  brand_model?: string
  color?: string
  type?: string
}

export interface DriverUpdateRequest {
  name?: string
  phone?: string
  vehicle_type?: string
  vehicle_plate?: string
  vehicle_color?: string
  vehicle_brand_model?: string
  engine_number?: string
  license_number?: string
  license_expiry?: string
  address?: string
  vehicle_inspection_date?: string
  status?: DriverStatus
  approval_status?: DriverApprovalStatus
  vehicles?: VehicleItem[]
}

export const driverApi = {
  getDrivers: async (
    page = 1,
    limit = 20,
    status?: DriverStatus,
  ) => {
    const params = new URLSearchParams()
    params.append('page', page.toString())
    params.append('limit', limit.toString())
    if (status) params.append('status', status)

    const response = await apiClient.get<PaginatedResponse<Driver>>(
      `${ENDPOINT}?${params.toString()}`,
    )
    return response.data
  },

  getDriverById: async (id: string) => {
    const response = await apiClient.get<Driver>(`${ENDPOINT}/${id}`)
    return response.data
  },

  createDriver: async (data: DriverCreateRequest) => {
    const response = await apiClient.post<Driver>(`${ENDPOINT}`, data)
    return response.data
  },

  updateDriver: async (id: string, data: DriverUpdateRequest) => {
    const response = await apiClient.patch<Driver>(`${ENDPOINT}/${id}`, data)
    return response.data
  },

  deleteDriver: async (id: string) => {
    await apiClient.delete(`${ENDPOINT}/${id}`)
  },

  updateStatus: async (id: string, status: DriverStatus) => {
    const response = await apiClient.patch<Driver>(`${ENDPOINT}/${id}/status`, { status })
    return response.data
  },

  updateApprovalStatus: async (id: string, approval_status: DriverApprovalStatus) => {
    const response = await apiClient.patch<Driver>(`${ENDPOINT}/${id}/approval`, { approval_status })
    return response.data
  },

  resetDriverPassword: async (driverId: string, newPassword: string) => {
    const response = await apiClient.post<{ message: string }>(`${ENDPOINT}/${driverId}/reset-password`, {
      new_password: newPassword,
    })
    return response.data
  },

  updateVehicles: async (id: string, vehicles: VehicleItem[]) => {
    const response = await apiClient.patch<Driver>(`${ENDPOINT}/${id}`, { vehicles })
    return response.data
  },

  setActiveVehicle: async (id: string, plate: string) => {
    const response = await apiClient.put<Driver>(`${ENDPOINT}/${id}/active-vehicle`, { plate })
    return response.data
  },
}
